import { getTime } from 'date-fns'
import clsx from 'clsx'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { match, P } from 'ts-pattern'

import Button from '@/components-latest/primitives/Button'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import { Download } from '@/components-latest/primitives/Icons'
import Spinner, { LoadingSpinner } from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import AcceptanceRate from '@/components-latest/ui/PerformancePage/AcceptanceRate'
import OrdersDoughnut from '@/components-latest/ui/PerformancePage/OrdersDoughnut'
import { OrdersPerformance as OrdersPerformanceData } from '@/ducks/performance/performance.types'
import { useLazyExportOrdersPerformanceQuery } from '@/ducks/performance/api/performance.api'
import { CalendarRange } from '@/components-latest/ui/PerformancePage/DateRangeSelect'
import { useToaster } from '@/components-latest/ui/Toaster/useToaster/useToaster'
import ExportLoadingNotification from './ExportLoadingNotification'

import Styles from './OrdersPerformance.module.scss'
import Store from '@/components-latest/primitives/Icons/Store'

interface IOrdersPerformanceProps {
  isFetching: boolean
  isLoading: boolean
  mode: 'single' | 'compare'
  branches: {
    label: string
    data: OrdersPerformanceData
  }[]
  dateRange?: CalendarRange | null
  storeId?: string
  isAllBranches?: boolean
}

const OrdersPerformance: React.FC<IOrdersPerformanceProps> = ({
  isFetching,
  isLoading,
  mode,
  branches,
  dateRange,
  storeId,
  isAllBranches = false
}) => {
  const { t } = useTranslation()
  const { addToast } = useToaster()
  const [exportOrders, { isFetching: isExporting }] = useLazyExportOrdersPerformanceQuery()

  const convertToCSV = (data: any[]) => {
    if (!data || data.length === 0) return ''

    const headers = Object.keys(data[0])
    
    const headerRow = headers.join(',')
    

    const dataRows = data.map(row => {
      return headers.map(header => {
        const value = row[header] || ''
        const stringValue = String(value).replace(/"/g, '""')
        return `"${stringValue}"`
      }).join(',')
    })
    
      return [headerRow, ...dataRows].join('\n')
  }

  const downloadCSV = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleExportCSV = async () => {
    if (!dateRange || !storeId) {
      addToast({
        type: 'system',
        intent: 'error',
        message: t('performance.orders.export-error-missing-params', 'Date range or store ID is missing')
      })
      return
    }

    try {
      const from = getTime(dateRange.startDate)
      const to = getTime(dateRange.endDate)
      
      const result = await exportOrders({ from, to }).unwrap()
      
      if (result?.data && result.data.length > 0) {
        const csvContent = convertToCSV(result.data)
        const filename = `orders-performance-${new Date().toISOString().split('T')[0]}.csv`
        downloadCSV(csvContent, filename)
        
        addToast({
          type: 'system',
          intent: 'success',
          message: t('performance.orders.export-success', 'CSV exported successfully')
        })
      } else {
        addToast({
          type: 'system',
          intent: 'warning',
          message: t('performance.orders.export-no-data', 'No data available to export')
        })
      }
    } catch (error) {
      // Show error notification
      addToast({
        type: 'system',
        intent: 'error',
        message: t('performance.orders.export-error', 'Failed to export CSV. Please try again.')
      })
    }
  }

  return (
    <div data-testid="orders-performance">
      {isExporting && (
        <ExportLoadingNotification
          message={t('performance.orders.export-processing', 'Your export is being processed...')}
        />
      )}
      <ContentBlock
        className={clsx(Styles.contentBlock, { [Styles.faded]: isFetching })}>
        {match({ isLoading })
          .with({ isLoading: true }, () => (
            <div className={Styles.spinnerWrapper}>
              <LoadingSpinner 
                containerType="centered" 
                text="Loading orders performance..."
              />
            </div>
          ))
          
          .otherwise(() => (
            
            <div
            className={
              mode === 'compare' ? Styles.branchCard : Styles.branchCard2
            }>
            {branches.map((branch, index: number) => (
              <React.Fragment key={index}>
                <div className={mode === 'compare' ? Styles.branchCard2 : Styles.singleBranchCard}>
                  {mode === 'compare' ? (
                    // Compare mode: centered layout
                    <>
                      <div className={Styles.sectionHeader}>
                        <div className={clsx(Styles.title, { [Styles.noTitle]: !branch.label })}>
                          {branch.label && (
                            <div className={Styles.icon}>
                              <Store />
                            </div>
                          )}
                          <Text variant="title-1" className={Styles.textlabel}>
                            {branch.label}
                          </Text>
                        </div>
                      </div>
                      <div className={Styles.ordersPerformance}>
                        <AcceptanceRate
                          received={branch.data.received}
                          rejected={branch.data.rejected}
                          accepted={branch.data.accepted}
                          className={Styles.rate}
                        />
                        <div className={Styles.doughnutWrapper}>
                          <div className={Styles.doughnut}>
                            <OrdersDoughnut.Chart ordersPerformance={branch.data} />
                          </div>
                          <OrdersDoughnut.Legend ordersPerformance={branch.data} />
                        </div>
                      </div>
                    </>
                  ) : (
                    // Single mode: horizontal layout
                    <>
                      <div className={Styles.sectionHeader}>
                        <div className={clsx(Styles.singleTitle, { [Styles.noTitle]: !branch.label })}>
                          {branch.label && (
                            <div className={Styles.icon}>
                              <Store />
                            </div>
                          )}
                          <Text variant="title-1" className={Styles.textlabel}>
                            {branch.label}
                          </Text>
                        </div>
                        {isAllBranches && (
                          <Button
                            variant="transparentOutlined"
                            size="small"
                            icon={<Download style={{ color: '#FB2576' }} />}
                            onClick={handleExportCSV}
                            className={Styles.exportButton}
                            disabled={isExporting || !dateRange || !storeId}>
                            {isExporting 
                              ? t('performance.orders.exporting', 'Exporting...')
                              : t('performance.orders.export-csv', 'Export as CSV')}
                          </Button>
                        )}
                      </div>
                      <div className={Styles.singleOrdersPerformance}>
                        <AcceptanceRate
                          received={branch.data.received}
                          rejected={branch.data.rejected}
                          accepted={branch.data.accepted}
                          className={Styles.singleRate}
                        />
                        <div className={Styles.singleDoughnutWrapper}>
                          <div className={Styles.doughnut}>
                            <OrdersDoughnut.Chart ordersPerformance={branch.data} />
                          </div>
                          <OrdersDoughnut.Legend ordersPerformance={branch.data} />
                        </div>
                      </div>
                    </>
                  )}
                </div>
          
                {/* Insert vertical line only between items */}
                {mode === 'compare' && index === 0 && branches.length > 1 && (
                  <div className={Styles.comparisonSeparator} />
                )}
              </React.Fragment>
            ))}
          </div>
          
          ))}
      </ContentBlock>
    </div>
  )
}

export default OrdersPerformance
